import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Globals } from "./globals";
import { MyFile } from "app/models/my-file";
import { Subject } from "rxjs";
import { UserService } from "app/services/user.service";
import { takeUntil, map, tap } from "rxjs/operators";
import { humanizeBytes } from "ng-uikit-pro-standard";
import { Resumable, ConfigurationHash } from "resumablejs";
import { FileComment } from "app/models/file-comment";
import moment from "moment";
import { DeletedFile } from "app/models/deleted-file";
import { FileVersion } from "app/models/file-version";
import { FormControl } from "@angular/forms";
import { initiateDownload } from "app/helpers/utils";
@Injectable({ providedIn: 'root' })
export class FileExplorerService {
    destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(private http: HttpClient, private globals: Globals, private userService: UserService) {

    }

    getNewResumableConfigForStorage(storageId: number) : ConfigurationHash {
        return <ConfigurationHash>{
            target: `${this.globals.api_base_url}/storage/${storageId}/upload_chunk`,
            query: { bearer: this.userService.access_token },
            headers: { Authorization: `Bearer ${this.userService.access_token}` },
            maxChunkRetries: 2,
            simultaneousUploads: 1, // Laravel chunk misses merging some chunks when parallel uploading is turned on
            chunkSize: 1 * 1024 * 1024,
            testChunks: true,
            prioritizeFirstAndLastChunk: false, // Laravel chunk misses merging intermediate chunks when last is sent first
            xhrTimeout: 300000,
            minFileSize: 0 as any,
        };
    }

    illegalFolderCharsValidator(c: FormControl) {
        let regex = /[\^`\{\}\[\]#%~<>:;,?"*|\/\\]/g;
        if (regex.test(c.value)) {
            return { folderNameIllegalChars: true }
        } else {
            return null;
        }
    }

    getAll(path: string, storageId: number) {
        const obj = { "path": path };
        return this.http.post<MyFile[]>(`${this.globals.api_base_url}/storage/${storageId}/list`, obj).pipe(map((files) => {
            files.forEach((file) => {
                file.type = (file.type == 'dir') ? "Folder" : file.type;

                if (file.size) {
                    file.formatted_size = humanizeBytes(+file.size);
                    file.size = +file.size;
                }
                else {
                    file.formatted_size = "..."
                }

                file.modified = file?.timestamp? new Date(+file.timestamp * 1000): null;
            })
            return files;
        }));
    }

    uploadFile(path: string, file: File, storageId: number, relativePath: string = null) {
        const obj = new FormData();
        obj.append("path", path);
        obj.append("file", file);
        if (relativePath != null)
            obj.append("file_name", relativePath);

        return this.http.post(`${this.globals.api_base_url}/storage/${storageId}/upload`, obj, { reportProgress: true, observe: 'events' }).pipe(takeUntil(this.destroy$));
    }

    createFolder(folder_name: string, path: string, storageId: number) {
        const obj = { "path": path, "folder_name": folder_name };
        return this.http.post(`${this.globals.api_base_url}/storage/${storageId}/create_folder`, obj);
    }

    deleteFolder(path: string, storageId: number) {
        const obj = { "path": path };
        return this.http.post(`${this.globals.api_base_url}/storage/${storageId}/delete_folder`, obj);
    }

    deleteFile(path: string, storageId: number) {
        const obj = { "path": path };
        return this.http.post(`${this.globals.api_base_url}/storage/${storageId}/delete`, obj);
    }

    copyFile(srcPath: string, destPath: string, storageId: number, destStorageId: number) {
        const obj = { "src_path": srcPath, "dest_path": destPath, "dest_storageId": destStorageId };
        return this.http.post(`${this.globals.api_base_url}/storage/${storageId}/copy`, obj);
    }

    copyFolder(srcPath: string, destPath: string, storageId: number, destStorageId: number) {
        const obj = { "src_path": srcPath, "dest_path": destPath, "dest_storageId": destStorageId };
        return this.http.post(`${this.globals.api_base_url}/storage/${storageId}/copy_folder`, obj);
    }

    moveFile(srcPath: string, destPath: string, storageId: number, destStorageId: number) {
        const obj = { "src_path": srcPath, "dest_path": destPath, "dest_storageId": destStorageId };
        return this.http.post(`${this.globals.api_base_url}/storage/${storageId}/move`, obj);
    }

    moveFolder(srcPath: string, destPath: string, storageId: number, destStorageId: number) {
        const obj = { "src_path": srcPath, "dest_path": destPath, "dest_storageId": destStorageId };
        return this.http.post(`${this.globals.api_base_url}/storage/${storageId}/move_folder`, obj);
    }

    getText(path: string, storageId: number) {
        return this.http.get<any>( `${this.globals.api_base_url}/storage/${storageId}/text?path=${encodeURIComponent(path)}`);
    }

    downloadFile(path: string, storageId: number) {
        initiateDownload(this.directFileURL(path, storageId));
    }

    downloadFileSpecificVersion(path: string, storageId: number, versionId: string) {
        initiateDownload(this.directFileDownloadURLSpecificVersion(path, storageId, versionId));
    }

    downloadFolder(path: string, storageId: number) {
        initiateDownload(`${this.globals.api_base_url}/storage/${storageId}/download_folder?path=${encodeURIComponent(path)}&bearer=${this.userService.access_token}`);
    }

    directFileURL(path: string, storageId: number) : string {
        return `${this.globals.api_base_url}/storage/${storageId}/download?path=${encodeURIComponent(path)}&bearer=${this.userService.access_token}`;
    }

    directFileDownloadURLSpecificVersion(path: string, storageId: number, versionId: string) : string {
        return `${this.globals.api_base_url}/storage/${storageId}/version/${versionId}/download?path=${encodeURIComponent(path)}&bearer=${this.userService.access_token}`;
    }

    directFileViewURLSpecificVersion(path: string, storageId: number, versionId: string) : string {
        return `${this.globals.api_base_url}/storage/${storageId}/version/${versionId}/view?path=${encodeURIComponent(path)}&bearer=${this.userService.access_token}`;
    }

    createZip(zip_path: string, paths: string[], storageId: number) {
        const obj = { "zip_path": zip_path, "paths": paths };

        return this.http.post(`${this.globals.api_base_url}/storage/${storageId}/zip`, obj);
    }
    getMetadata(storageId: number, path: string) {
        return this.http.get<{[key: string]: string}>( `${this.globals.api_base_url}/storage/${storageId}/metadata?path=${encodeURIComponent(path)}`).pipe(map((o) => {
            var result: {[key: string]: string|number|Date} = {};
            Object.keys(o).forEach((k) => {
                var value = o[k].substring(1);
                if (o[k].substring(0, 1) == 'n') {
                    result[k] = +(value == ''? '0': value);
                } else if (o[k].substring(0, 1) == 'd') {
                    result[k] = value == ''? '': new Date(value);
                } else {
                    // treat as string
                    result[k] = (value);
                }
            });
            return result;
        }));
    }
    postMetadata(storageId: number, paths: string[], fileClass: string, metadata: any) {
        return this.http.post( `${this.globals.api_base_url}/storage/${storageId}/metadata`, {
            paths: paths,
            file_class: fileClass,
            metadata: JSON.stringify(metadata || {})
        });
    }
    listDeletedFiles(storageId: number) {
        return this.http.get<DeletedFile[]>( `${this.globals.api_base_url}/storage/${storageId}/deleted-file`);
    }

    restoreDeletedFile(storageId: number, path: string) {
        return this.http.post( `${this.globals.api_base_url}/storage/${storageId}/deleted-file/restore`, { path: path });
    }

    listFileVersions(storageId: number, path: string) {
        return this.http.get<FileVersion[]>( `${this.globals.api_base_url}/storage/${storageId}/version?path=${encodeURIComponent(path)}`);
    }

    restoreFileVersion(storageId: number, path: string, versionId: string) {
        return this.http.post(`${this.globals.api_base_url}/storage/${storageId}/version/${versionId}/restore`, { path: path});
    }

    //#region File Comments 
    getFileComment(storageId: number, path: string) {
        return this.http.get<FileComment[]>( `${this.globals.api_base_url}/storage/${storageId}/comments?path=${encodeURIComponent(path)}`);
    }
    getUserAvatarURL(userid): string {
        return `${this.globals.api_base_url}/user/avatar/${userid}`;
    }
    addFileComment(comment: FileComment) {
       
        return this.http.post<any>(`${this.globals.api_base_url}/storage/${comment.storage_id}/comment`, comment);
    }
    UpdateFileComment(comment: FileComment) {
        return this.http.put<any>(`${this.globals.api_base_url}/storage/${comment.storage_id}/comment/${comment.id}`, comment);
    }
    deleteFileComment(storageId: number, commentId: number) {
        return this.http.delete<any>(`${this.globals.api_base_url}/storage/${storageId}/comment/${commentId}`);

    }
    //#endregion
}